import { useEffect, useMemo, useState } from 'react'
import { useNodeModels } from '@/hooks/useNodeModels'
import {
  SUGGESTED_SHADER_MODELS,
  listGrokModels,
  listLocalModels,
  listOpenRouterModels,
} from '@/lib/canvas/shader/authoring'
import { DEFAULT_LOCAL_BASE_URL, type ShaderProviderConfig, type ShaderProviderId, type ShaderProviderSecrets } from '@/lib/canvas/shader/authoring/types'

export interface ShaderModelOption {
  id: string
  label: string
}

/**
 * Model lists come back as strings or `{ id, label | name }` objects, sometimes
 * wrapped in `{ data }` or `{ models }`; this reads any of them.
 */
export function normalizeModelOptions(list: unknown): ShaderModelOption[] {
  if (list && typeof list === 'object' && !Array.isArray(list)) {
    const wrapped = (list as { data?: unknown; models?: unknown }).data ?? (list as { models?: unknown }).models
    return wrapped === undefined ? [] : normalizeModelOptions(wrapped)
  }
  if (!Array.isArray(list)) return []
  const options: ShaderModelOption[] = []
  for (const item of list) {
    if (typeof item === 'string') {
      if (item) options.push({ id: item, label: item })
      continue
    }
    if (!item || typeof item !== 'object') continue
    const record = item as Record<string, unknown>
    const id = typeof record.id === 'string' ? record.id : typeof record.name === 'string' ? record.name : ''
    if (!id) continue
    const label = typeof record.label === 'string' ? record.label : typeof record.name === 'string' ? record.name : id
    options.push({ id, label })
  }
  return options
}

/** The shortlist a provider starts with, before (or without) a live listing. */
export function suggestedShaderModels(provider: ShaderProviderId): ShaderModelOption[] {
  const all = SUGGESTED_SHADER_MODELS as unknown
  if (Array.isArray(all)) return normalizeModelOptions(all)
  return normalizeModelOptions((all as Record<string, unknown> | null)?.[provider])
}

function mergeOptions(...lists: ShaderModelOption[][]): ShaderModelOption[] {
  const seen = new Set<string>()
  return lists.flat().filter((option) => (seen.has(option.id) ? false : (seen.add(option.id), true)))
}

export interface ShaderModelOptions {
  options: ShaderModelOption[]
  loading: boolean
  /** Why the live list could not be read; the suggestions and free text still work. */
  error: string | null
}

type Listing = { key: string; options: ShaderModelOption[]; error: string | null }

/**
 * Models the chosen provider offers, read live: Genie's hosted text models when
 * OpenRouter is paid with credits (and nothing else), else the provider's own
 * listing after the suggestions.
 */
export function useShaderModelOptions(config: ShaderProviderConfig, secrets: ShaderProviderSecrets): ShaderModelOptions {
  const credits = config.provider === 'openrouter' && (config.billing ?? 'genie-credits') === 'genie-credits'
  const hosted = useNodeModels('text-llm', undefined, 'create', undefined, credits)
  const baseUrl = config.baseUrl || DEFAULT_LOCAL_BASE_URL
  const grokKey = secrets.grok ?? ''
  const listingKey = credits ? '' : `${config.provider}|${config.provider === 'local' ? baseUrl : ''}|${config.provider === 'grok' ? grokKey : ''}`
  const [listing, setListing] = useState<Listing>({ key: '', options: [], error: null })

  useEffect(() => {
    if (!listingKey) return
    if (config.provider === 'grok' && !grokKey) {
      setListing({ key: listingKey, options: [], error: null })
      return
    }
    const controller = new AbortController()
    const request: Promise<unknown> =
      config.provider === 'local' ? listLocalModels(baseUrl, controller.signal)
        : config.provider === 'grok' ? listGrokModels(grokKey, controller.signal)
          : listOpenRouterModels(controller.signal)
    request
      .then((list) => { if (!controller.signal.aborted) setListing({ key: listingKey, options: normalizeModelOptions(list), error: null }) })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setListing({ key: listingKey, options: [], error: cause instanceof Error ? cause.message : 'Could not list models' })
      })
    return () => controller.abort()
  }, [listingKey, config.provider, baseUrl, grokKey])

  return useMemo(() => {
    if (credits) {
      // Only what the credits route streams: enabled OpenRouter rows of Genie's catalogue. No suggestions are
      // merged in: a slug outside the catalogue can be priced yet refused after the wallet is charged.
      const models = hosted.data
        .filter((m) => m.provider === 'openrouter' && m.enabled !== false)
        .map((m) => ({ id: m.endpointId || m.id, label: m.name }))
      return { options: mergeOptions(models), loading: hosted.isLoading, error: hosted.error?.message ?? null }
    }
    const suggested = suggestedShaderModels(config.provider)
    const current = listing.key === listingKey ? listing : null
    return { options: mergeOptions(suggested, current?.options ?? []), loading: current === null, error: current?.error ?? null }
  }, [config.provider, credits, hosted.data, hosted.isLoading, hosted.error, listing, listingKey])
}
